import type { CallFollowUp } from "shared/app/CallFollowUp";
import { User } from "views/settings-users/slices/users/types";
import type { LandXDeclineReasonCode } from "api/landlord-surveys/land-x.types";

export enum NotificationKind {
  Mention = "mention",
  Assigned = "assigned",
  Message = "message",
  Call = "call",
  PropertyShield = "property_shield",
  AiAgent = "ai_agent",
  // CRM-1769 Bring your number: date set, rejected, live, canceled, expired.
  PhoneNumbers = "phone_numbers",
  RentalVerification = "rental_verification",
  Application = "application",
  // PLT-725: a property manager asked an admin to add a Boom product.
  Products = "products"
}
// Page tabs. Communications holds two API kinds (message, call) as sub-tabs.
export enum NotificationTab {
  Application = "application",
  Mention = "mention",
  Assigned = "assigned",
  PropertyShield = "property_shield",
  PhoneNumbers = "phone_numbers",
  Products = "products",
  RentalVerification = "rental_verification",
  Communication = "communication"
}

export enum CommunicationSubTab {
  Messages = "message",
  Calls = "call"
}

export enum NotificationType {
  TaskMention = "mentioned_in_tasks",
  TaskNoteMention = "mentioned_in_task_notes",
  ApplicationNotesMention = "mentioned_in_application_notes",
  LeadNotesMention = "mentioned_in_lead_notes",
  AssignedTask = "task_assigned",
  DueTask = "task_due_soon",
  LeadAssigned = "lead_assigned",
  ApplicationAssigned = "application_assigned",
  InboxMessage = "inbox_message",
  InboxMessageDigest = "inbox_message_digest",
  MissedCall = "missed_call",
  // CRM-1777: replaces missed_call for a call that left a voicemail.
  Voicemail = "voicemail",
  ListingFraudAlertDetected = "listing_fraud_alert_detected",
  EscalatedConversation = "escalated_conversation",
  SmsNotDelivered = "sms_not_delivered",
  AiCallEscalated = "ai_call_escalated",
  AiCallFollowUp = "ai_call_follow_up",
  CallerNeedsHelp = "caller_needs_help",
  PhoneNumberPort = "phone_number_port",
  ProductRequested = "product_requested",
  RentalVerificationRequested = "rental_verification_requested",
  IdvBypassRequested = "idv_bypass_requested",
  ShowingReschedulePicked = "showing_reschedule_picked",
  ShowingRescheduleNoneWork = "showing_reschedule_none_work",
  ShowingRescheduleReminder = "showing_reschedule_reminder"
}

export const SHOWING_RESCHEDULE_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.ShowingReschedulePicked,
  NotificationType.ShowingRescheduleNoneWork,
  NotificationType.ShowingRescheduleReminder
];

export enum InboxMessageChannel {
  Email = "email",
  Sms = "sms",
  Chat = "chat"
}

export type RentalVerificationCardState =
  | "new"
  | "started"
  | "completed"
  | "declined"
  | "closed";

export interface RentalVerificationNotificationState {
  state: RentalVerificationCardState;
  survey_status: string | null;
  started_by_name: string | null;
  submitted_by_name: string | null;
  decline_reason_code: LandXDeclineReasonCode | null;
  decline_reason: string | null;
}

export interface Notification {
  id: number;
  kind: NotificationKind;
  created_at: string;
  type: NotificationType;
  user: User;
  author?: User | string;
  read_at: string | null;
  url: string;
  updated_at: string;
  rich_text?: {
    document_html: string;
    document_text: string;
  } | null;
  rental_verification?: RentalVerificationNotificationState;
  metadata: {
    rich_text_id?: number;
    application_id?: number | null;
    rich_text_field?: "title" | "description";
    note_id?: number;
    task_id?: number | null;
    lead_id?: number | null;
    // inbox_message metadata
    channel?: InboxMessageChannel;
    conversation_id?: string;
    contact_id?: number;
    message_id?: string;
    preview?: string;
    sender_name?: string;
    property_name?: string;
    // escalated_conversation metadata (CRM-1438). `reason` (shared with
    // ai_call_escalated below) is "direct_request" | "frustration";
    // `contact_name` labels the card.
    contact_name?: string;
    attempt?: number;
    reason_short?: string;
    // inbox_message_digest metadata
    count?: number;
    channels?: InboxMessageChannel[];
    conversation_ids?: string[];
    window_start?: string;
    window_end?: string;
    messages?: Array<{
      conversation_id?: string;
      channel?: InboxMessageChannel;
      preview?: string;
      sender_name?: string;
      property_name?: string;
      occurred_at?: string;
      read_at?: string | null;
    }>;
    // missed_call metadata (CRM-1235). `from_number` is the caller — the number
    // "Call back" dials; `to_name` is the partner-side label the title reads as
    // "Missed call at {to_name}". `ring_seconds` is ended_at - started_at, not
    // Twilio's CallDuration, which is 0 for an unanswered call.
    call_id?: number;
    customer_id?: number | null;
    from_number?: string;
    to_number?: string;
    from_name?: string | null;
    to_name?: string | null;
    occurred_at?: string;
    ring_seconds?: number | null;
    // voicemail metadata (CRM-1777): same call fields as missed_call, plus the
    // voicemail's length and the first line of its transcript (null when
    // transcription failed).
    voicemail_duration_seconds?: number | null;
    transcript_preview?: string | null;
    status?: string;
    direction?: string;
    alert_id?: number;
    unit_id?: number | null;
    listing_url?: string;
    listing_platform?: string;
    property_shield_url?: string | null;
    unit_name?: string | null;
    listed_for_cents?: number | null;
    location?: string | null;
    city_state?: string | null;
    // ai_call_escalated metadata (CRM-1472): the AI agent call (transcription
    // row) whose drawer the notification opens, and who called which number.
    // ai_call_follow_up (CRM-1593) uses the same call_transcript_id.
    call_transcript_id?: number;
    caller_number?: string | null;
    agent_number?: string | null;
    reason?: string | null;
    // CRM-1616: how the AI agent call was escalated, and who an email
    // escalation went to.
    escalation_channel?: "transfer" | "email" | null;
    email_recipients?: string[] | null;
    property_id?: number | null;
    // Set when the AI agent escalated the call (CRM-1393). `from_number` equal
    // to `transferred_from_number` means the caller stayed unknown.
    transferred_from_number?: string | null;
    transferred_from_name?: string | null;
    // ai_call_follow_up metadata (CRM-1593). `caller_quote` is the caller's
    // own words from the transcript; every other label stays generic.
    agent_name?: string | null;
    caller_name?: string | null;
    caller_phone_number?: string | null;
    caller_quote?: string | null;
    escalation_label?: string | null;
    follow_up?: CallFollowUp | null;
    was_transferred?: boolean;
    transferred_to_number?: string | null;
    // phone_number_port metadata (CRM-1769). `title` and `body` are written by
    // the API; the card shows them as they are.
    port_request_id?: number;
    port_kind?: string;
    number_ids?: number[];
    phone_numbers?: string[];
    // product_requested metadata (PLT-725). `title` and `body` are written by
    // the API; the card shows them as they are.
    product_request_id?: number;
    product_key?: string;
    product_name?: string;
    requested_by_name?: string | null;
    // rental_verification_requested metadata: the tenancy the
    // applicant listed at one of this partner's properties.
    survey_id?: number;
    survey_token?: string;
    routed_partner_id?: number;
    property_address?: string | null;
    unit?: string | null;
    rental_address?: string;
    move_in_date?: string | null;
    move_out_date?: string | null;
    applicant_id?: number;
    verification_id?: number;
    bypass_request_id?: number;
    applicant_name?: string | null;
    idv_vendor?: "plaid" | "persona" | null;
    resolved?: boolean;
    showing_id?: number | null;
    title?: string | null;
    body?: string | null;
  };
}

export interface MarkAllAsReadRequest {
  kind: NotificationKind;
}

export interface MarkAllAsReadResponse {
  count: number;
  total_unread: number;
}

export interface NotificationGroup {
  name: string;
  items: Notification[];
}
